import { FormEvent, useMemo, useState } from 'react'
import { Mail, Smartphone } from 'lucide-react'
import { Button, Modal, Tile, TileContent, TileDescription, TileHeader, TileTitle } from '../ui'

type MfaMethod = 'totp' | 'email'

interface MfaChallengeProps {
  methods: string[]
  emailMask?: string | null
  isLoading?: boolean
  onSubmit: (payload: { method: MfaMethod; code: string; trustDevice: boolean; deviceLabel?: string }) => Promise<void> | void
  onRequestEmailCode: () => Promise<void> | void
  onStartTotpSetup: () => Promise<{ qrDataUrl: string }> | { qrDataUrl: string }
  onActivateTotpSetup: (payload: { code: string; trustDevice: boolean; deviceLabel?: string }) => Promise<void> | void
}

export default function MfaChallenge({
  methods,
  emailMask,
  isLoading = false,
  onSubmit,
  onRequestEmailCode,
  onStartTotpSetup,
  onActivateTotpSetup
}: MfaChallengeProps) {
  const [method, setMethod] = useState<MfaMethod>(methods.includes('totp') ? 'totp' : 'email')
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(true)
  const [deviceLabel, setDeviceLabel] = useState('')
  const [showTotpSetupModal, setShowTotpSetupModal] = useState(false)
  const [totpQrDataUrl, setTotpQrDataUrl] = useState<string | null>(null)
  const [totpSetupCode, setTotpSetupCode] = useState('')

  const methodTitle = useMemo(() => {
    if (method === 'totp') return 'Code aus Authenticator App'
    return 'Code aus E-Mail'
  }, [method])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await onSubmit({
      method,
      code,
      trustDevice,
      deviceLabel: deviceLabel.trim() || undefined
    })
    setCode('')
  }

  const handleStartTotpSetup = async () => {
    const result = await onStartTotpSetup()
    if (result?.qrDataUrl) {
      setTotpQrDataUrl(result.qrDataUrl)
      setTotpSetupCode('')
      setShowTotpSetupModal(true)
    }
  }

  const handleActivateTotpSetup = async () => {
    await onActivateTotpSetup({
      code: totpSetupCode,
      trustDevice,
      deviceLabel: deviceLabel.trim() || undefined
    })
  }

  return (
    <Tile className="mx-auto w-full max-w-md">
      <TileHeader>
        <TileTitle as="h2" className="text-base">
          Zwei-Faktor-Anmeldung
        </TileTitle>
        <TileDescription>Bitte bestaetige die Anmeldung mit einem zusaetzlichen Code.</TileDescription>
      </TileHeader>
      <TileContent className="space-y-4">
        {!methods.includes('totp') && (
          <div className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800 dark:border-primary-800/60 dark:bg-primary-950/30 dark:text-primary-200">
            Tipp: Fuer den Alltag ist <strong>FreeOTP / FreeOTP+</strong> meist am bequemsten.
            Du kannst ihn direkt jetzt per QR-Code verbinden.
            <div className="mt-2">
              <Button type="button" size="sm" variant="outline" onClick={handleStartTotpSetup}>
                FreeOTP jetzt verbinden (QR)
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {methods.includes('totp') && (
            <Button type="button" variant={method === 'totp' ? 'primary' : 'outline'} size="sm" onClick={() => setMethod('totp')}>
              <Smartphone className="mr-1 h-4 w-4" />
              App-Code
            </Button>
          )}
          {methods.includes('email') && (
            <Button type="button" variant={method === 'email' ? 'primary' : 'outline'} size="sm" onClick={() => setMethod('email')}>
              <Mail className="mr-1 h-4 w-4" />
              E-Mail-Code
            </Button>
          )}
        </div>

        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
          {methodTitle}
          {method === 'email' && emailMask ? ` (${emailMask})` : ''}
        </div>

        {method === 'email' && (
          <Button type="button" variant="secondary" size="sm" onClick={() => onRequestEmailCode()} disabled={isLoading}>
            E-Mail-Code anfordern
          </Button>
        )}

        <form className="space-y-3" onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="123456"
            className="input-field w-full"
            autoComplete="one-time-code"
            required
          />
          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(event) => setTrustDevice(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Dieses Geraet fuer 60 Tage merken
          </label>
          {trustDevice && (
            <input
              type="text"
              value={deviceLabel}
              onChange={(event) => setDeviceLabel(event.target.value)}
              placeholder="Optional: z. B. MacBook Lehrerzimmer"
              className="input-field w-full"
            />
          )}
          <Button type="submit" className="w-full" disabled={isLoading}>
            Code pruefen
          </Button>
        </form>
      </TileContent>

      <Modal
        isOpen={showTotpSetupModal}
        onClose={() => setShowTotpSetupModal(false)}
        title="FreeOTP verbinden"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setShowTotpSetupModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleActivateTotpSetup} disabled={!totpSetupCode.trim()}>
              Verbinden und anmelden
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {totpQrDataUrl ? (
            <img src={totpQrDataUrl} alt="FreeOTP QR Code" className="mx-auto h-48 w-48 rounded border border-gray-200 bg-white p-2" />
          ) : null}
          <input
            type="text"
            value={totpSetupCode}
            onChange={(event) => setTotpSetupCode(event.target.value)}
            placeholder="Code aus FreeOTP"
            className="input-field w-full"
          />
        </div>
      </Modal>
    </Tile>
  )
}
